import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useState } from "react";
import { EyeIcon } from "@heroicons/react/24/outline";
import Lightbox from "../components/detailProduct/zoomImg";

const apiUrl = process.env.REACT_APP_API_URL;


export default function DetailProduct() {
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // Récupération de l'id du produit dans l'URL
  const { id } = useParams();

  // Contexte du panier
  const { cart, addToCart } = useCart();
  const currentQtyInCart = (size: string) =>
    cart
      .filter((i) => i._id === product._id && i.size === size)
      .reduce((acc, i) => acc + i.quantity, 0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; visible: boolean }>({
    msg: "",
    visible: false,
  });

  const showToast = (msg: string) => {
    setToast({ msg, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2000);
  };

  const [lightbox, setLightbox] = useState<{ open: boolean; idx: number }>({
    open: false,
    idx: 0,
  });
  const openLb = (i: number) => setLightbox({ open: true, idx: i });
  const closeLb = () => setLightbox({ open: false, idx: 0 });

  const {
    data: product,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["product", id],
    queryFn: () => fetchProduct(id!),
    enabled: !!id, // évite d'appeler si id est undefined
  });

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (isLoading) return <div>Chargement...</div>;
  if (isError || !product) return <div>Erreur lors du chargement</div>;


  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        {/* Image */}
        <div className="relative">
          <img
            src={product.images[currentIndex]}
            alt={product.title}
            onClick={() => openLb(currentIndex)}
            className="w-full h-[450px] object-cover border-2 border-slate-600 rounded-md cursor-zoom-in"
            loading="lazy"
          />
          <button
            onClick={handlePrev}
            className="absolute left-[-20px] top-1/2 -translate-y-1/2 text-2xl font-bold rounded transition-transform duration-200 transform hover:scale-125"
          >
            &#x276E;
          </button>

          <button
            onClick={handleNext}
            className="absolute right-[-20px] top-1/2 -translate-y-1/2 text-2xl font-bold rounded transition-transform duration-200 transform hover:scale-125"
          >
            &#x276F;
          </button>
        </div>

        {/* Infos produit dynamiques */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{product.title}</h2>
          <p className="text-gray-700 font-medium">{product.description}</p>
          <p className="text-2xl font-bold">{product.price}€</p>

          {product.specs && product.specs.length > 0 && (
            <ul className="mt-4 list-disc list-inside text-sm text-gray-700 space-y-1">
              {product.specs.map((line: any, i: any) => (
                <li key={i}>{line}</li>
              ))}
              <li>
                <span className="inline-flex items-center gap-1">
                  Dimensions&nbsp;:
                  <button
                    onClick={() => setShowSizeGuide(true)}
                    className="group relative inline-flex items-center"
                  >
                    <EyeIcon className="w-5 h-5 text-gray-600 group-hover:text-black transition" />

                    <span
                      className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap
                         bg-black text-white text-xs rounded px-2 py-1 opacity-0
                         group-hover:opacity-100 transition pointer-events-none"
                    >
                      Voir le guide des tailles
                    </span>
                  </button>
                </span>
              </li>
            </ul>
          )}

          {/* Couleurs */}
          <div>
            <p className="text-sm font-medium mb-1">COULEUR(S)</p>
            <div className="flex gap-2">
              {product.colors.map((color: string, i: number) => (
                <div
                  key={i}
                  className={`w-5 h-5 rounded-full border ${
                    color === "white"
                      ? "bg-white border-gray-400"
                      : `bg-${color}`
                  }`}
                ></div>
              ))}
            </div>
          </div>

          {/* Tailles */}
          <div>
            <p className="text-sm font-medium mb-1 mt-4">TAILLES</p>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((size: string) => {
                const totalStock = product.stockBySize?.[size] ?? 0;
                const already = currentQtyInCart(size);
                const remaining = totalStock - already;
                const isDisabled = remaining <= 0;

                return (
                  <button
                    key={size}
                    onClick={() => !isDisabled && setSelectedSize(size)}
                    disabled={isDisabled}
                    className={`border px-3 py-1 text-sm rounded-md transition
        ${selectedSize === size ? "bg-black text-white" : "hover:bg-gray-100"}
        ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {size}
                    {remaining <= 3 &&
                      remaining > 0 &&
                      ` (stock: ${remaining})`}
                    {remaining === 0 && ` (épuisé)`}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bouton */}

          <button
            onClick={() => {
              if (!selectedSize) return;
              const totalStock = product.stockBySize?.[selectedSize] ?? 0;
              const already = currentQtyInCart(selectedSize);
              if (totalStock - already <= 0) return;

              addToCart({
                _id: product._id,
                title: product.title,
                price: product.price,
                image: product.images[0],
                size: selectedSize,
                paymentLinkUrl: product.paymentLinkUrl,
              });

              showToast("Article ajouté au panier ✅");
            }}
            disabled={!selectedSize}
            className={`mt-6 px-6 py-2 rounded-md w-full transition
    ${
      selectedSize
        ? "bg-black text-white hover:bg-gray-800"
        : "bg-gray-300 text-gray-500 cursor-not-allowed"
    }`}
          >
            Ajouter au panier
          </button>
        </div>
      </div>
      {toast.visible && (
        <div className="fixed top-[60%] right-6 z-50 bg-black text-white px-4 py-3 rounded-md shadow-lg transition-opacity duration-200">
          {toast.msg}
        </div>
      )}

      {showSizeGuide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-lg w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermeture */}
            <button
              onClick={() => setShowSizeGuide(false)}
              className="absolute top-1 right-1 text-black hover:text-white hover:bg-black rounded-full px-1 transition"
              title="Fermer"
            >
              ✕
            </button>

            <img
              src="/images/taille/table_taille.png"
              alt="Tableau des dimensions"
              className="w-full h-auto rounded"
              loading="lazy"
            />
          </div>
        </div>
      )}

      {lightbox.open && (
        <Lightbox
          images={product.images}
          openIdx={lightbox.idx}
          onClose={closeLb}
        />
      )}
    </section>
  );
}

async function fetchProduct(id: string) {
  const res = await fetch(`${apiUrl}/api/products/${id}`);
  if (!res.ok) throw new Error("Erreur de récupération");
  return res.json();
}
